import { PropertyFieldsEdit } from "./property-fields-edit";
import { PropertyFieldsView } from "./property-fields-view";
import { useCallback, useEffect, useState } from "react";
import { PropertyField, PropertyType } from "types/property";
import { apiRequest } from "utils/api";
import { Flex, Spinner } from "@chakra-ui/react";
import { useTenant } from "modules/tenant/providers/tenant-provider";

interface PropertyFieldsAmplendProps {
  propertyId: number;
}

export function PropertyFieldsAmplend({ propertyId }: PropertyFieldsAmplendProps) {
  const { tenantId } = useTenant();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [property, setProperty] = useState<PropertyType | null>(null);
  const [propertyDetailFields, setPropertyDetailFields] = useState<PropertyField[]>([]);

  const getPropertyDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest("/supabase/properties/single", {
        id: propertyId,
        companyId: tenantId,
      });

      setProperty(response);

      const currentPropertyDetails = await apiRequest(
        "/supabase/properties/property_detail/select",
        { id: propertyId }
      );

      setPropertyDetailFields(
        currentPropertyDetails.property_fields.map((field: any) => ({
          ...field,
          id: field.id.toString(), // Ensure id is a string
        }))
      );
    } catch (error) {
      // Error is handled by the apiRequest utility
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, tenantId]);

  useEffect(() => {
    getPropertyDetails();
  }, [getPropertyDetails]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <Spinner />
      </Flex>
    );
  }

  return isEditing ? (
    <PropertyFieldsEdit
      property={property}
      propertyDetailFields={propertyDetailFields}
      setPropertyDetailFields={setPropertyDetailFields}
      onSave={handleSave}
      onCancel={handleCancel}
      isEditMode={isEditing}
    />
  ) : (
    <PropertyFieldsView
      property={property}
      propertyDetailFields={propertyDetailFields}
      onEditClick={handleEditClick}
    />
  );
}
